import { ref, computed } from "vue";
import { useArtworkStore } from "@/store/artwork";
import { queryArtworkModuleVersion } from "../service";
import { useModelStore } from "@/store/model";
import { getURLSearchParams, jsonParse } from "@/utils";
import { createDefaultName } from '@/utils/defaultName';
import router from "@/router";
import { type RouteLocationNormalized } from "vue-router";
import { getCkptTypeSubValue } from "@/views/model/data";
import type { CkptType } from "@/types/model";
import { useGeneratingVersions } from "@/hooks/useGeneratingVersions";
import { useSmartPromptFinishModule } from "./useSmartPromptFinishModule";
import { getUserModelId } from "./common";
import { handleFirstModelUpdate } from "@/utils/model";
import { useTaskQueue2 } from "@/hooks/usetaskQueue2";
import { defaultModelId } from '@/assets/artwork';
import { isExploreA3Ckpt } from '@/assets/artwork/module/a3/applyExploreA3Params';
import { isAiVideoModule, isExpansionModuleId, resolveStoreModuleId } from '@/assets/artwork/moduleEnum';
import { applyLegacyRenderParamsMigration } from '@/hooks/useRenderStyleInit';
import { mapAiSeedanceRecordToParams } from '@/views/workspace/components/ModulePanel/AiSeedancePanel/composables/aiVideoConfig';

export const paramsDrawerCollapse = ref(false);
export const historyDrawerCollapse = ref(false);
import { useModelViewerStore, WorkType } from "@/store/modelViewer";
import { useNewUserGuide } from "@/store/newUserGuide";

export const beforeEnterWorkspace = async (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  try {
    const artworkStore = useArtworkStore();
    const modelStore = useModelStore();
    const modelViewerStore = useModelViewerStore();
    modelViewerStore.isLeaveWorkspace = false;

    modelStore.open = false;

    const userStore = useUserStore();
    userStore.isFromOfficialWebsite = getURLSearchParams("route_from") === "official-website";
    userStore.deviceEnv = getURLSearchParams("device_env") || "";
    /**
     * 【问卷调查】只会在工作台首页/home目录下弹出
     * https://www.teambition.com/task/6938e923eed6c0aef24a6a43
     */
    // let hasCollect = await userStore.getCustom("userCollectModalKey");

    // if (userStore.isFromOfficialWebsite) {
    //   if (userStore.deviceEnv === "pc" && userStore.userInfo.firstLogin) {
    //     if (!hasCollect) {
    //       userStore.newUserInfoCollectForBModalOpen = true;
    //     }
    //   }
    // }

    // 没有指定生图模块 先重定向到一键成稿
    if (to.name === "workspace") {
      router.replace({
        name: "explore"
      });
    }

    if (artworkStore.artworkState.id) {
      const { data } = await queryArtworkModuleVersion({
        artworkId: artworkStore.artworkState.id
      });

      // 获取当前作品未完成版本
      const { init } = useGeneratingVersions();
      init(artworkStore.artworkState.id);

      // 没有指定生图模块，并且有 lastModule时，进入lastModule
      if (to.name === "workspace" && data?.lastModule) {
        let moduleId = data.lastModule;

        // 兼容旧版智能编辑模块 6 重定向到涂抹替换
        if (moduleId === 6) {
          moduleId = ModuleId.Replace;
        }

        // 归一化工作流模块 id 到 store 路由模块 id
        // 重绘放大(59)->zoom(20)、智能扩图V2(50)->expansion(22)、智能编辑V2(51)->replace(18)
        // 这些工作流 module id 在 holopix-types 中有独立 routeKey，但工作台路由表只注册了归一化后的路由
        moduleId = resolveStoreModuleId(moduleId);

        const moduleRouteKey = getModuleRouteKeyById(moduleId);

        if (moduleRouteKey) {
          router.replace({
            name: moduleRouteKey
          });
        }
      }

      // 更新作品信息
      const artworkState: Record<string, any> = {
        artworkName: data?.artworkName || artworkStore.artworkState.artworkName
      };
      if (!artworkStore.artworkState.modelList) {
        artworkState.modelList = jsonParse(data?.modelList) || [];
      }

      await artworkStore.updateArtwork(artworkState);

      if (data?.draftParam) {
        artworkStore.resetArtworkModule();
        const params = jsonParse(data?.draftParam);


        await applyLegacyRenderParamsMigration(params);

        // 老视频模块(25)参数格式与 AiSeedancePanel 不兼容，编辑时跳过参数复用
        if (Number(params.module) !== Number(ModuleId.Aivideo)) {
          if (!artworkStore.skipResetParamsModules.includes(params.module)) {
            artworkStore.resetModuleParams(params.module as any, params);
          }

          if (!artworkStore.skipResetResultModules.includes(params.module)) {
            artworkStore.resetModuleResult(params.module as unknown as keyof typeof data.module);
          }
        }
      }

      if (data?.module) {
        artworkStore.resetArtworkModule();

        for (const key in data.module) {
          const info = data.module[key as unknown as keyof typeof data.module] as any;
          const params = jsonParse(info.param);

          // 画面拓展模式下，参考图片读取originLayoutRefer
          if (isExpansionModuleId(Number(key))) {
            if (params?.originLayoutRefer) {
              params.layoutRefer = { ...params.originLayoutRefer };
            }
            if (params?._referenceImage) {
              params._referenceImage = [...params._referenceImage];
              params.maskContrastImage = params._referenceImage[1];
            }
          }

          /**
           * 处理3d模块
           */

          // 只生成了模型，没有生成结果
          if (Number(key) === ModuleId.GenerateThreeTransform && modelViewerStore.isGenerateThreeDFinish) {
            const { threeDImg, threeDOriginImgUrl, threeDRmbgImg } = params;
            const { images } = info;

            modelViewerStore.modelId = images[0].id;
            modelViewerStore.modelCollect = images[0].collect;
            modelViewerStore.modelUrl = threeDImg || images[0].glb;
            modelViewerStore.imageUrl = threeDOriginImgUrl;
            modelViewerStore.removeBackgroundImageUrl = threeDRmbgImg;
            setTimeout(() => {
              // 非3D转换时，收起参数面板
              modelViewerStore.workType = WorkType.THREE_TRANSFORM;
            }, 1000);
          }

          if (params.module === ModuleId.Aivideo) {
            info.images = [
              {
                id: info.id,
                width: 0,
                height: 0,
                image: info.videoUrl,
                clientId: info.clientId,
                versionId: info.id
              }
            ];
          }

          // AI 创作视频 Seedance 2.0 模块（75-78）：重组 images 为视频格式，
          // 并将 API 格式参数（videoMode/videoResolution/videoRatio）映射为面板格式（mode/quality/ratio）
          if (isAiVideoModule(params.module as number) && params.module !== ModuleId.Aivideo) {
            info.images = [
              {
                id: info.id,
                width: 0,
                height: 0,
                image: info.videoUrl,
                clientId: info.clientId,
                versionId: info.id
              }
            ];
            const panelPatch = mapAiSeedanceRecordToParams(params);
            Object.assign(params, panelPatch);
          }

          if (artworkStore.skipResetParamsModules.includes(Number(key)) || Number(key) === 6) {
            continue;
          }

          // 文俊特殊处理，当使用了3视图的姿势时，默认在提示词后面加上,reference sheet,但回显时,不添加
          const isThreeView = [25, 26, 27, 28, 29, 30].includes(params?.layoutReferPose?.id);
          if (isThreeView) {
            // 去掉尾部的,reference sheet,
            params.prompt = params.prompt.replace(/,reference sheet,?$/, "");
          }

          artworkStore.resetModuleParams(key as unknown as keyof typeof data.module, params);

          artworkStore.resetModuleResult(key as unknown as keyof typeof data.module, {
            data: {
              status: info.status,
              images: info.images,
              param: info.param
            },
            msg: "seccess",
            progress: 100,
            code: 3
          });

          artworkStore.resetModuleLastVersionParams(key as unknown as keyof typeof data.module, params);
        }
      }
    } else {
      // 自动生成一个初始作品名称
      artworkStore.initArtworkName = getArtworkName();
      artworkStore.artworkState.artworkName = artworkStore.initArtworkName;
    }

    if (artworkStore.artworkState.modelList.length === 0) {
      if (modelStore.ignore) {
        modelStore.open = true;
        modelStore.ignore = false;
      } else {
        let modelId = 0;

        if (!userStore.isLogin) {
          modelId = defaultModelId;
        } else {
        // 获取用户最近使用的模型
        // 新手引导时，使用新手引导默认模型
        const newUserGuideStore = useNewUserGuide();
        const newUserGuideAbTest = useNewUserGuideAbTest();
        
        if (newUserGuideAbTest.isBUser.value) {
          modelId = newUserGuideStore.guideForBModelId || (await getUserModelId());
        } else {
          modelId = newUserGuideStore.isNeedShow ? guideModelId : await getUserModelId();
        }
        }


        await artworkStore.updateArtwork({
          modelList: [{ id: modelId }] as any
        });

        modelStore.resetModalState();
        modelStore.activeModelId = modelId;
      }
    } else if (artworkStore.firstModel.loraType === "Controller") {
      modelStore.firshIsController = true;
      modelStore.activeCkptType = getCkptTypeSubValue(artworkStore.firstModel.ckptType) as CkptType;
      modelStore.open = true;
    }

    handleFirstModelUpdate();

    if (artworkStore.skipResetParamsModules.length > 0) {
      artworkStore.skipResetParamsModules = [];
    }
    if (artworkStore.skipResetResultModules.length > 0) {
      artworkStore.skipResetResultModules = [];
    }

    // 刷新页面更新模型信息（使用 Navigation API 真正检测浏览器刷新，避免误判内部路由跳转）
    const isPageReload = (window.performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';
    if (isPageReload) {
      await artworkStore.updateArtwork({
        modelList: artworkStore.artworkState.modelList
      });
    }

    // 会员用户进入作品自动切换到加速通道
    if (userStore.isMemberUser) {
      artworkStore.channel = "expedite";
    } else {
      artworkStore.channel = "normal";
    }
  } catch (error) {
    console.log(error);
  }
};

export async function updateArtworkState(): Promise<void> {
  return new Promise(async (resolve) => {
    const artworkStore = useArtworkStore();

    const { data } = await queryArtworkModuleVersion({
      artworkId: artworkStore.artworkState.id
    });

    const modelList = data?.modelList ? jsonParse(data?.modelList) : [];

    await artworkStore.updateArtwork({
      artworkName: data?.artworkName || artworkStore.artworkState.artworkName,
      modelList: artworkStore.artworkState.modelList || modelList
    });

    if (data?.draftParam) {
      artworkStore.resetArtworkModule();
      const params = jsonParse(data?.draftParam);

      await applyLegacyRenderParamsMigration(params);

      if (!artworkStore.skipResetParamsModules.includes(params.module)) {
        artworkStore.resetModuleParams(params.module as unknown as keyof typeof data.module, params);
      }

      if (!artworkStore.skipResetResultModules.includes(params.module)) {
        artworkStore.resetModuleResult(params.module as unknown as keyof typeof data.module);
      }
    }

    if (data?.module) {
      artworkStore.resetArtworkModule();

      for (const key in data.module) {
        if (artworkStore.skipResetParamsModules.includes(Number(key))) {
          continue;
        }

        const info = data.module[key as unknown as keyof typeof data.module] as any;
        const params = jsonParse(info.param);

        if (Number(key) === 6) {
          continue;
        }

        artworkStore.resetModuleParams(key as unknown as keyof typeof data.module, params);

        artworkStore.resetModuleResult(key as unknown as keyof typeof data.module, {
          data: {
            status: info.status,
            images: info.images,
            param: info.param
          },
          msg: "seccess",
          progress: 100,
          code: 3
        });

        artworkStore.resetModuleLastVersionParams(key as unknown as keyof typeof data.module, params);
      }
    }

    resolve();
  });
}

export const beforeRouteLeaveWorkspace = () => {
  const { resetArtwork } = useArtworkStore();
  resetArtwork();

  const modelStore = useModelStore();
  const modelViewerStore = useModelViewerStore();

  modelStore.open = false;
  modelStore.resetModalState();

  paramsDrawerCollapse.value = false;
  historyDrawerCollapse.value = false;

  modelViewerStore.isGenerateThreeDFinish = true;
  modelViewerStore.threeDGenerateParams = null;
  modelViewerStore.isLeaveWorkspace = true;

  // 清理任务队列定时器
  const { cleanup } = useTaskQueue2();
  cleanup();

  const { clear: clearSmartPromptFinishModule } = useSmartPromptFinishModule();
  clearSmartPromptFinishModule();
};


import { guideModelId } from "@/assets/artwork/default";
import { useUserStore } from "@/store/user";
import { useNewUserGuideAbTest } from "@/hooks/abTest/useNewUserGuideAbTest";
import { ModuleId } from "@xmiles/holopix-types";
import { getModuleRouteKeyById } from "@xmiles/holopix-helper";

export function getArtworkName() {
  return createDefaultName('未命名');
}

// 当模型分类为 道具图标、场景时，人物姿势不可见，一键成稿创意草图为必填项
// A3 无人物姿态能力，参考区固定为「参考图（线稿参考/原图参考）」形态，与 Flux 同走单参考分支
export const modelNotPose = computed(() => {
  const artworkStore = useArtworkStore();
  const modelList = artworkStore.artworkState.modelList;

  if (modelList.length > 0) {
    return [3, 4].includes(modelList[0].category) || artworkStore.isFlux || isExploreA3Ckpt(modelList[0].ckptType);
  }
  return false;
});

// 只有模型分类为通用、角色时，才显示面部精修
export const useFaceDetail = computed(() => {
  const artworkStore = useArtworkStore();
  if (artworkStore.artworkState.modelList.length > 0) {
    if ([1, 2].includes(artworkStore.artworkState.modelList[0].category)) {
      return true;
    }
  }

  return false;
});

// 只有角色立绘分类才会使用面部精修
export function categoryHasFaceDetail(category: number) {
  if (category) {
    return category === 2;
  }
  return false;
}

// 只有首个模型的loraType为非SDXL时，才显示形象样式参考
export const useProfilReference = computed(() => {
  const artworkStore = useArtworkStore();
  if (artworkStore.firstModel) {
    const ckptTypeSubValue = getCkptTypeSubValue(artworkStore.artworkState.modelList[0].ckptType);
    return ckptTypeSubValue?.includes("SDXL") || ckptTypeSubValue === "A1";
  }

  return false;
});

// 只有首个模型的loraType为SDXL时，才显示扰乱注意力模式
export const useDisruptions = computed(() => {
  const artworkStore = useArtworkStore();

  if (artworkStore.firstModel) {
    const ckptType = getCkptTypeSubValue(artworkStore.artworkState.modelList[0].ckptType);
    return !["SDXL", "Flux"].includes(ckptType);
  }
  return false;
});
